import site from "./site.json";

/**
 * Single source of truth for site-wide SEO constants.
 *
 * Values come from site.json and may be overridden at build time with
 * REACT_APP_SITE_URL (useful for staging deployments, where the canonical
 * host differs from production).
 *
 * Everything here must be safe to evaluate during prerendering, i.e. it may
 * never touch `window`. Deriving the origin from `window.location` would bake
 * the prerender host (localhost) into the generated HTML.
 */
const rawSiteUrl = process.env.REACT_APP_SITE_URL || site.siteUrl;

/** Canonical origin, never with a trailing slash. e.g. "https://onlineskolemad.dk" */
export const SITE_URL = rawSiteUrl.replace(/\/+$/, "");

export const BRAND = site.brand;
export const LEGAL_NAME = site.legalName;
export const LOCALE = site.locale;
export const LANG = site.lang;
export const CVR = site.cvr;
export const APP_STORE_URL = site.appStoreUrl;
export const PLAY_STORE_URL = site.playStoreUrl;
export const TWITTER_CARD = site.twitterCard;

/**
 * Normalises a route path into a canonical, absolute URL.
 *
 * Canonical policy: no trailing slash anywhere except the root, query strings
 * and hashes stripped. This keeps /menu, /menu/ and /menu?x=1 collapsing onto
 * a single canonical URL.
 */
export function absoluteUrl(path = "/") {
  if (/^https?:\/\//i.test(path)) return path;

  const [withoutHash] = String(path).split("#");
  const [pathname] = withoutHash.split("?");

  let clean = pathname.startsWith("/") ? pathname : `/${pathname}`;
  clean = clean.replace(/\/+$/, "");

  return clean === "" ? `${SITE_URL}/` : `${SITE_URL}${clean}`;
}

/** Absolute URL for an asset that lives in /public. */
export function assetUrl(pathOrUrl) {
  if (!pathOrUrl) return absoluteUrl(site.defaultOgImage);
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

export const DEFAULT_OG_IMAGE = assetUrl(site.defaultOgImage);
export const LOGO_URL = assetUrl(site.logo);

export default {
  SITE_URL,
  BRAND,
  LEGAL_NAME,
  LOCALE,
  LANG,
  DEFAULT_OG_IMAGE,
  LOGO_URL,
  absoluteUrl,
  assetUrl,
};
